import moment from 'moment-timezone';

// Status presentation. Full class strings (no dynamic construction) so Tailwind
// keeps them at build time. Uses the standard palette with dark variants since
// the extended status-* design tokens don't exist on this branch.
export const STATUS_META = {
  new: {
    label: 'New',
    badge:
      'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-200',
    dot: 'bg-slate-400',
  },
  attempting: {
    label: 'Attempting',
    badge:
      'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  engaged: {
    label: 'Engaged',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
    dot: 'bg-blue-500',
  },
  nurture: {
    label: 'Nurture',
    badge:
      'bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-300',
    dot: 'bg-purple-500',
  },
  invited: {
    label: 'Invited',
    badge:
      'bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300',
    dot: 'bg-violet-500',
  },
  converted: {
    label: 'Converted',
    badge:
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  dropped: {
    label: 'Dropped',
    badge: 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300',
    dot: 'bg-rose-500',
  },
};

// Pipeline order for the status dropdown / grouping.
export const STATUS_ORDER = [
  'new',
  'attempting',
  'engaged',
  'nurture',
  'invited',
  'converted',
  'dropped',
];

// The subset a user can set by hand in the status dropdown. invited/converted/
// dropped are system-driven (invite send / registration / delete). Mirrors
// MANUAL_LEAD_STATUSES on the server.
export const MANUAL_STATUS_ORDER = ['new', 'attempting', 'engaged', 'nurture'];

export const statusMeta = (status) =>
  STATUS_META[status] || {
    label: status || 'Unknown',
    badge: 'bg-muted text-secondary-foreground',
    dot: 'bg-muted-foreground',
  };

// Outcome for a History-tab lead. A converted client is Working / Completed /
// Withdrawn (derived from their claims); a deleted lead is Dropped. Shown as a
// badge + filter chip in the History tab.
export const OUTCOME_META = {
  working: {
    label: 'Working',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
    dot: 'bg-blue-500',
  },
  completed: {
    label: 'Completed',
    badge:
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  withdrawn: {
    label: 'Withdrawn',
    badge: 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300',
    dot: 'bg-rose-500',
  },
  dropped: {
    label: 'Dropped',
    badge: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-500/20 dark:text-zinc-300',
    dot: 'bg-zinc-400',
  },
};

export const outcomeMeta = (outcome) =>
  OUTCOME_META[outcome] || {
    label: outcome || 'Unknown',
    badge: 'bg-muted text-secondary-foreground',
    dot: 'bg-muted-foreground',
  };

// Pretty-print a raw claim status string (e.g. "in process" -> "In Process").
export const titleCase = (value) =>
  typeof value === 'string' && value
    ? value.replace(/\b\w/g, (c) => c.toUpperCase())
    : '';

export const fullName = (lead) =>
  `${lead?.leadFirstName || ''} ${lead?.leadLastName || ''}`.trim() || 'Lead';

export const initials = (lead) => {
  const f = (lead?.leadFirstName || '').trim();
  const l = (lead?.leadLastName || '').trim();
  const out = `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
  return out || '?';
};

export const assigneeName = (lead) => {
  const n =
    `${lead?.assignedToFirstName || ''} ${lead?.assignedToLastName || ''}`.trim();
  return n || lead?.assignedToEmail || 'Unassigned';
};

// "Today" / "Yesterday" / "Jun 16, 2026" — relative, friendly, never crashes on
// a null/invalid date.
export const friendlyDate = (value) => {
  if (!value) return null;
  const m = moment(value);
  if (!m.isValid()) return null;
  const today = moment().startOf('day');
  const that = m.clone().startOf('day');
  const diff = today.diff(that, 'days');
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff === -1) return 'Tomorrow';
  return m.format('MMM D, YYYY');
};

export const friendlyDateTime = (value) => {
  if (!value) return null;
  const m = moment(value);
  if (!m.isValid()) return null;
  const day = friendlyDate(value);
  return `${day} at ${m.format('h:mm A')}`;
};

export const isOverdue = (lead) => {
  if (!lead?.leadNextFollowUpAt || lead.leadFollowUpCompletedAt) return false;
  const m = moment(lead.leadNextFollowUpAt);
  return m.isValid() && m.isBefore(moment());
};

// The follow-up's display state: completed (done) / overdue (past due) /
// upcoming (future) / null (none). Drives the tile + detail colors.
export const followUpState = (lead) => {
  if (!lead?.leadNextFollowUpAt) return null;
  if (lead.leadFollowUpCompletedAt) return 'completed';
  const m = moment(lead.leadNextFollowUpAt);
  if (!m.isValid()) return null;
  return m.isBefore(moment()) ? 'overdue' : 'upcoming';
};

// Whole days since the lead was last worked = since the last outreach touch, or
// since creation if never touched. Powers the "aging" tile badge + No-Touch /
// Aging filters. Calendar-day granularity (matches friendlyDate).
export const daysSinceActivity = (lead) => {
  const ref = lead?.leadLastTouchAt || lead?.leadCreatedOn;
  if (!ref) return null;
  const m = moment(ref);
  if (!m.isValid()) return null;
  return moment().startOf('day').diff(m.clone().startOf('day'), 'days');
};

// --- Source / channel ---
// Partner leads carry leadSourceSystem ('envoy' | 'referral' | 'event' |
// 'partner_qr'); everything else is a plain web/phone/email lead.
export const isEnvoyReferral = (lead) =>
  lead?.leadSource === 'partner' && lead?.leadSourceSystem === 'envoy';
export const isReferralProgram = (lead) =>
  lead?.leadSource === 'partner' && lead?.leadSourceSystem === 'referral';
// In-person event channel (partners rows with partnerType='event').
export const isEventLead = (lead) => lead?.partnerType === 'event';
// A verified partner's own QR/landing-page scan (2026-08-20).
export const isPartnerQrLead = (lead) =>
  lead?.leadSource === 'partner' && lead?.leadSourceSystem === 'partner_qr';

// The synthetic umbrella partner all unattributed referral leads ride under.
const REFERRAL_PROGRAM_SLUG = 'referral-program';

// A referral lead still under the umbrella partner (no referrer has graduated to
// their own partner yet). Once a referrer signs up, graduation repoints the
// lead's partner to a personal row, so the slug changes and this turns false.
// Absent slug (e.g. older data) is treated as umbrella so it degrades safely.
export const isReferralProgramUmbrella = (lead) =>
  isReferralProgram(lead) &&
  (!lead?.partnerReferralUrl ||
    lead.partnerReferralUrl === REFERRAL_PROGRAM_SLUG);

// Friendly source label for the board. Every partner channel uses the unified
// "{partnerName} - Referral" form (e.g. "Envoy - Referral", "Jane Doe - Referral").
// Umbrella referral-program leads use the referrer's name (always collected
// since 2026-07-22); "Anonymous - Referral" only remains for pre-change leads
// whose referredBy is null.
export const sourceLabel = (lead) => {
  if (isReferralProgramUmbrella(lead)) {
    const referrer = referrerOf(lead);
    return referrer ? `${referrer.name} - Referral` : 'Anonymous - Referral';
  }
  if (isEventLead(lead) && lead?.partnerName) {
    return `${lead.partnerName} - Event`;
  }
  if (lead?.partnerName) return `${lead.partnerName} - Referral`;
  if (isEnvoyReferral(lead)) return 'Envoy - Referral';
  return (
    lead?.leadMeta?.siteName ||
    lead?.leadSourceSystem ||
    lead?.leadSource ||
    '—'
  );
};

// The individual website referrer (referral-program leads). The detail endpoint
// returns `referredBy` at the top level; the list returns it inside leadMeta.
export const referrerOf = (lead) => {
  const r = lead?.referredBy || lead?.leadMeta?.referredBy || null;
  if (!r) return null;
  const name = `${r.firstName || ''} ${r.lastName || ''}`.trim();
  return name ? { ...r, name } : null;
};

// Format a phone string as (xxx) xxx-xxxx when it looks like a US 10-digit.
export const formatPhone = (raw) => {
  if (!raw) return '';
  const d = String(raw).replace(/\D/g, '');
  if (d.length === 10)
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith('1'))
    return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return raw;
};
